import useSWR from "swr";
import { APP_VERSION } from "./version.js";

// bin/push.sh writes build/version.json next to the bundle and uploads it
// with no-cache headers, so this URL always reports what is currently deployed
// rather than what this tab happens to be running. It is per-bucket, so the
// test channel compares against its own deploys, not production's.
const VERSION_URL = "/version.json";

// SWR caches by key, so every caller of useVersionCheck() shares one cache entry
// and one poll.
const VERSION_KEY = "app-version-check";

// `npm start` and hand-run builds leave APP_VERSION as "dev" and never publish a
// version.json, so there is nothing to compare against and polling would just
// 404 forever. Only push.sh sets APP_VERSION, and it always publishes a
// version.json alongside, so any other value implies a real deploy.
const CHECK_ENABLED = APP_VERSION !== "dev";

const POLL_MS = 300000;

const fetchDeployedVersion = async () => {
  // The query param defeats the browser cache. The no-cache header set on the S3
  // object is what defeats CloudFront, whose default cache policy ignores query
  // strings entirely.
  const resp = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
    cache: "no-store",
  });
  if (!resp.ok) throw new Error(`version.json responded ${resp.status}`);
  return resp.json();
};

export const useVersionCheck = () => {
  const { data, isValidating, mutate } = useSWR(
    CHECK_ENABLED ? VERSION_KEY : null,
    fetchDeployedVersion,
    // SWR revalidates on window focus by default, which is the trigger that
    // actually matters: coming back to a tab left open across a deploy.
    { refreshInterval: POLL_MS, errorRetryCount: 3 },
  );

  const deployedVersion = data?.version;

  return {
    enabled: CHECK_ENABLED,
    deployedVersion,
    // Compared with !== rather than "is newer": any difference means this tab
    // isn't running what is deployed, and reloading is the right move after a
    // rollback just as much as after a release. Staying false while
    // deployedVersion is undefined keeps a 404 or a network blip from ever
    // putting a banner on screen.
    isStale: !!deployedVersion && deployedVersion !== APP_VERSION,
    isChecking: isValidating,
    checkNow: mutate,
  };
};
